import { type IndicatorKind } from './indicator-colors'
import { CompletionDots, type CompletionDotsItem } from './completion-dots'

export type ScoreRowItem = {
  /** Stable key — used for React + activeKey matching. */
  key: string
  /** Color/glyph kind for this dim. */
  kind: IndicatorKind
  /** Whether the underlying test/survey was passed (drives the colored vs muted dot). */
  passed: boolean
  /** When true, the dim is on the auto-generated standard (not custom-described); rendered slightly faded. */
  standard?: boolean
  /**
   * The integrant has this indicator defined even though no comparable score exists (e.g. the
   * position dimension is on the standard profile). Paints a faded coloured background instead of
   * the empty dashed pill, so "has the indicator" reads differently from "never tested".
   */
  defined?: boolean
  /** Optional numeric score. Shown in the tooltip rows when present. */
  score?: number | null
  /** Translated label. */
  label: string
}

/**
 * Per-dim exact scores + global score, in a single inline cluster.
 *
 * Thin wrapper over `CompletionDots`: maps the dims (+ global summary) into completion items and
 * renders them via the chosen `variant` — `scores` (colour-coded score badges, `activeKey`
 * highlighted) or `dots` (status dots + the active dim's and global score inline). The whole
 * cluster shares one tooltip listing every indicator and global.
 * Fits inline alongside an `<ItemTitle>` and is the canonical score display for sidebar list rows.
 *
 * Generic over the indicator domain — pass any `items[]` (test dims, org surveys, etc.) and
 * an `activeKey` to highlight the sorted one (`'global'` to highlight the global summary).
 */
export function ScoreRow(props: {
  items: ScoreRowItem[]
  /** Key of the selected dim — that badge gets the highlight. Pass `'global'` to mark the global summary as the selected sort instead. */
  activeKey?: string
  /** Global summary badge, rendered after the dims; highlighted only when `activeKey === 'global'`. */
  global?: { score?: number | null; label: string }
  className?: string
  /** `scores` (default): per-indicator score badges. `dots`: status dots + active/global score inline. */
  variant?: 'dots' | 'scores'
  /** Force every score into the muted "no comparison" state. See `CompletionDots.muted`. */
  muted?: boolean
}) {
  const variant = props.variant ?? 'scores'
  const muted = props.muted ?? false
  const global = props.global
  const hasGlobal = global?.score != null

  const dotsItems: CompletionDotsItem[] = props.items.map((i) => ({
    key: i.key,
    kind: i.kind,
    completed: i.passed,
    standard: i.standard ?? null,
    defined: i.defined ?? null,
    score: i.score,
    label: i.label,
  }))

  // Fold the global summary into the same list so the whole cluster shares one tooltip.
  const tooltipItems: CompletionDotsItem[] = global
    ? [
        ...dotsItems,
        {
          key: 'global',
          kind: 'global',
          completed: hasGlobal,
          standard: null,
          score: global.score,
          label: global.label,
        },
      ]
    : dotsItems

  return (
    <CompletionDots
      items={tooltipItems}
      variant={variant}
      activeKey={props.activeKey}
      muted={muted}
      tooltipSide="bottom"
      className={props.className}
    />
  )
}
